import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';

export interface ImportRowResult {
  row: number;
  status: 'created' | 'updated' | 'skipped' | 'error';
  id?: number;
  error?: string;
}

export interface ImportSummary {
  created: number;
  updated: number;
  skipped: number;
  errors: number;
}

export interface ImportResponse {
  summary: ImportSummary;
  results: ImportRowResult[];
}

export interface ImportOptions {
  dryRun?: boolean;
  updateMode?: 'upsert' | 'insert_only' | 'update_only';
  createInitialInspection?: boolean;
  inspectionDate?: string;
}

export interface ColumnMapping {
  csvColumn: string;
  h3gearField: string | null;
}

export const H3GEAR_ASSET_FIELDS = [
  { value: 'serial_number', label: 'Serial Number', required: true },
  { value: 'name', label: 'Name', required: false },
  { value: 'status', label: 'Status', required: false },
  { value: 'manufacturer', label: 'Manufacturer', required: false },
  { value: 'model_number', label: 'Model Number', required: false },
  { value: 'type', label: 'Type', required: false },
  { value: 'purchase_date', label: 'Purchase Date', required: false },
  { value: 'service_entry_date', label: 'Service Entry Date', required: false },
  { value: 'length_m', label: 'Length (m)', required: false },
  { value: 'safe_working_load', label: 'Safe Working Load', required: false },
  { value: 'inspection_interval_days', label: 'Inspection Interval (days)', required: false },
  { value: 'maintenance_interval_days', label: 'Maintenance Interval (days)', required: false },
  { value: 'notes', label: 'Notes', required: false },
] as const;

export type H3GearFieldValue = typeof H3GEAR_ASSET_FIELDS[number]['value'];

const COMMON_COLUMN_MAPPINGS: Record<string, H3GearFieldValue> = {
  'serial_number': 'serial_number',
  'serialnumber': 'serial_number',
  'serial': 'serial_number',
  'serial no': 'serial_number',
  'serial no.': 'serial_number',
  'sn': 'serial_number',
  'name': 'name',
  'asset name': 'name',
  'asset_name': 'name',
  'description': 'name',
  'status': 'status',
  'manufacturer': 'manufacturer',
  'mfg': 'manufacturer',
  'make': 'manufacturer',
  'brand': 'manufacturer',
  'model': 'model_number',
  'model_number': 'model_number',
  'modelnumber': 'model_number',
  'model number': 'model_number',
  'model no': 'model_number',
  'model no.': 'model_number',
  'type': 'type',
  'asset type': 'type',
  'asset_type': 'type',
  'category': 'type',
  'purchase_date': 'purchase_date',
  'purchasedate': 'purchase_date',
  'purchase date': 'purchase_date',
  'date purchased': 'purchase_date',
  'bought date': 'purchase_date',
  'service_entry_date': 'service_entry_date',
  'serviceentrydate': 'service_entry_date',
  'service entry date': 'service_entry_date',
  'in service date': 'service_entry_date',
  'length': 'length_m',
  'length_m': 'length_m',
  'length (m)': 'length_m',
  'rope length': 'length_m',
  'swl': 'safe_working_load',
  'safe_working_load': 'safe_working_load',
  'safe working load': 'safe_working_load',
  'working load': 'safe_working_load',
  'inspection_interval_days': 'inspection_interval_days',
  'inspection interval': 'inspection_interval_days',
  'inspection_interval': 'inspection_interval_days',
  'maintenance_interval_days': 'maintenance_interval_days',
  'maintenance interval': 'maintenance_interval_days',
  'maintenance_interval': 'maintenance_interval_days',
  'notes': 'notes',
  'comments': 'notes',
  'remarks': 'notes',
};

export function autoDetectColumnMapping(csvColumns: string[]): ColumnMapping[] {
  return csvColumns.map((col) => {
    const normalizedCol = col.toLowerCase().trim();
    const mappedField = COMMON_COLUMN_MAPPINGS[normalizedCol] || null;
    return {
      csvColumn: col,
      h3gearField: mappedField,
    };
  });
}

export function useImportAssets() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      columnMapping,
      options,
    }: {
      file: File;
      columnMapping: ColumnMapping[];
      options: ImportOptions;
    }): Promise<ImportResponse> => {
      const text = await file.text();
      const lines = text.split('\n');
      if (lines.length === 0) {
        throw new Error('CSV file is empty');
      }

      const headerLine = lines[0];
      const originalHeaders = parseCSVLine(headerLine);
      
      const mappedHeaders = originalHeaders.map((header) => {
        const mapping = columnMapping.find((m) => m.csvColumn === header);
        return mapping?.h3gearField || header;
      });

      const mappedLines = [mappedHeaders.join(','), ...lines.slice(1)];
      const mappedCsv = mappedLines.join('\n');

      const params = new URLSearchParams();
      if (options.dryRun) {
        params.set('dry_run', 'true');
      }
      if (options.updateMode) {
        params.set('update_mode', options.updateMode);
      }
      if (options.createInitialInspection) {
        params.set('create_initial_inspection', 'true');
        if (options.inspectionDate) {
          params.set('inspection_date', options.inspectionDate);
        }
      }

      const formData = new FormData();
      formData.append('file', new Blob([mappedCsv], { type: 'text/csv' }), 'assets.csv');

      const response = await apiClient.post<ImportResponse>(
        `/api/v1/import/assets/?${params.toString()}`,
        formData,
        {
          headers: { 'Content-Type': undefined } as Record<string, string | undefined>,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
    },
  });
}

export function useDownloadTemplate() {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await apiClient.get('/api/v1/export/templates/assets/', {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'assets_import_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current.trim());
  return result;
}

export function parseCSVPreview(text: string, maxRows: number = 10): { headers: string[]; rows: string[][] } {
  const lines = text.split('\n').filter((line) => line.trim());
  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }

  const headers = parseCSVLine(lines[0]);
  const rows = lines.slice(1, maxRows + 1).map((line) => parseCSVLine(line));

  return { headers, rows };
}

export interface GroupedErrors {
  message: string;
  rows: number[];
  count: number;
}

export function groupImportErrors(results: ImportRowResult[]): GroupedErrors[] {
  const errorMap = new Map<string, number[]>();
  
  for (const result of results) {
    if (result.status === 'error' && result.error) {
      const rows = errorMap.get(result.error) || [];
      rows.push(result.row);
      errorMap.set(result.error, rows);
    }
  }

  return Array.from(errorMap.entries())
    .map(([message, rows]) => ({
      message,
      rows,
      count: rows.length,
    }))
    .sort((a, b) => b.count - a.count);
}

export function generateErrorReport(results: ImportRowResult[]): string {
  const errors = results.filter((r) => r.status === 'error');
  if (errors.length === 0) {
    return '';
  }

  const lines = ['Row,Error'];
  for (const error of errors) {
    const escapedError = error.error?.replace(/"/g, '""') || 'Unknown error';
    lines.push(`${error.row},"${escapedError}"`);
  }

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Pilot data import hooks
// ---------------------------------------------------------------------------

export type PilotEntityType = 'techs' | 'tech-certifications' | 'clients' | 'projects';

export interface PilotImportOptions {
  dryRun?: boolean;
  updateMode?: 'upsert' | 'insert_only' | 'update_only';
}

function usePilotImport(entityType: PilotEntityType, invalidateKeys: readonly string[]) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      options,
    }: {
      file: File;
      options: PilotImportOptions;
    }): Promise<ImportResponse> => {
      const params = new URLSearchParams();
      if (options.dryRun) {
        params.set('dry_run', 'true');
      }
      if (options.updateMode) {
        params.set('update_mode', options.updateMode);
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post<ImportResponse>(
        `/api/v1/import/${entityType}/?${params.toString()}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      if (!variables.options.dryRun) {
        for (const key of invalidateKeys) {
          queryClient.invalidateQueries({ queryKey: [key] });
        }
      }
    },
  });
}

export function useImportTechs() {
  return usePilotImport('techs', ['crew', 'techs']);
}

export function useImportTechCertifications() {
  return usePilotImport('tech-certifications', ['certifications', 'crew']);
}

export function useImportClients() {
  return usePilotImport('clients', ['project-clients']);
}

export function useImportProjects() {
  return usePilotImport('projects', ['projects']);
}

export function useDownloadPilotTemplate(entityType: PilotEntityType) {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await apiClient.get(`/api/v1/export/templates/${entityType}/`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${entityType}_import_template.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}
